import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Page,
  Hero,
  HeroGlow,
  HeroContent,
  HeroLeft,
  BackBtn,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  HeroActions,
  RefreshIconButton,
  CreateBtn,
  StatsStrip,
  StatChip,
  StatIcon,
  StatValue,
  StatLabel,
  ErrorBanner,
  FormAlertBox,
  CreatePanel,
  PanelHeader,
  PanelTitleRow,
  PanelTitle,
  PanelSubtitle,
  CloseCreatorBtn,
  CreateBodyGrid,
  FormColumn,
  PreviewColumn,
  Field,
  LabelRow,
  Label,
  FieldHint,
  HelperText,
  CharCounter,
  Input,
  FieldGrid2,
  BodyToolbar,
  InsertVarBtn,
  ToolbarInfo,
  TextArea,
  SampleValuesSection,
  SectionTitleBar,
  InfoIconWrap,
  SectionTitle,
  SectionSubtitle,
  SampleInputsGrid,
  SampleField,
  SampleLabel,
  ButtonAddRow,
  ButtonTypeToggle,
  ButtonTypeOption,
  ButtonInputsGrid,
  ButtonHelpBox,
  AddButtonBtn,
  ButtonChipsList,
  ButtonChip,
  RemoveChipBtn,
  FormActions,
  SecondaryBtn,
  PrimaryBtn,
  SpinningIcon,
  PreviewContainer,
  PhoneHeader,
  PhoneAvatar,
  PhoneTitle,
  PhoneStatus,
  PhoneChatArea,
  ChatBubble,
  ChatBody,
  PlaceholderText,
  ChatFooter,
  ChatMeta,
  DoubleCheckMark,
  PreviewButtonsList,
  PreviewButtonPill,
  PreviewFooterNote,
  ListPanel,
  SearchFilterRow,
  SearchBox,
  SearchInput,
  ClearSearchBtn,
  FilterTabs,
  FilterTabBtn,
  ListBody,
  EmptyWrap,
  TemplateListGrid,
  TemplateCard,
  TemplateTop,
  TemplateHeaderLeft,
  TemplateDisplayName,
  PillsRow,
  CategoryBadge,
  LanguageBadge,
  StatusBadge,
  TemplatePurposeBox,
  TemplatePurposeText,
  TemplateBodyBox,
  TemplateFooterBox,
  TemplateButtonsRow,
  MiniButtonChip,
  TemplateActionsBar,
  ActionBtn,
  DeleteActionBtn,
  LaunchCampaignShortcutBtn,
  ModalBackdrop,
  ModalBox,
  ModalHeaderRow,
  ModalHeading,
  ModalText,
  ModalBtnRow,
  DeleteConfirmBtn,
} from "./TemplateManagerStyles";
import {
  FileText,
  Plus,
  ArrowLeft,
  Sparkles,
  X,
  CheckCircle2,
  Clock,
  XCircle,
  Eye,
  RefreshCw,
  Trash2,
  Send,
  HelpCircle,
  AlertCircle,
  Search,
  Globe,
  ExternalLink,
  MessageSquare,
} from "lucide-react";
import EmptyState from "../../../atoms/Dashboard/WhatsApp/EmptyState";
import { useTemplates } from "../../../../hooks/useTemplates";
import { SkeletonListRows } from "../../../atoms/Dashboard/WhatsApp/SkeletonLoader";
import type { WhatsAppTemplate, WhatsAppTemplateButton } from "../../../../Data/whatsapp.dto";
import { showErrorToast, showSuccessToast } from "../../../atoms/Utlis/Toast";

const STATUS_TABS = [
  { value: "all", label: "All Templates" },
  { value: "approved", label: "Approved (Ready)" },
  { value: "pending", label: "In Review" },
  { value: "rejected", label: "Rejected" },
] as const;

const TemplateManagerPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shouldOpenCreate = searchParams.get("create") === "true";
  const {
    templates,
    loading,
    error,
    isError,
    syncingTemplateId,
    deletingTemplateId,
    refresh,
    createTemplate,
    syncTemplate,
    deleteTemplate,
  } = useTemplates();

  // Create form state
  const [showCreate, setShowCreate] = useState(shouldOpenCreate);

  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setShowCreate(true);
    }
  }, [searchParams]);
  const [templateName, setTemplateName] = useState("");
  const [messagePurpose, setMessagePurpose] = useState("");
  const [body, setBody] = useState("");
  const [sampleValues, setSampleValues] = useState<Record<number, string>>({});
  const [footer, setFooter] = useState("");
  const [buttons, setButtons] = useState<WhatsAppTemplateButton[]>([]);
  const [buttonType, setButtonType] = useState<"QUICK_REPLY" | "URL">("QUICK_REPLY");
  const [newButtonText, setNewButtonText] = useState("");
  const [newButtonUrl, setNewButtonUrl] = useState("");

  // List filter state
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Delete modal state
  const [deleteModalTemplate, setDeleteModalTemplate] = useState<WhatsAppTemplate | null>(null);

  // Form error tracking
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Detect variables in body: {{1}}, {{2}}, etc.
  const detectedVariables = useMemo(() => {
    const matches = Array.from(body.matchAll(/\{\{(\d+)\}\}/g));
    const indices = Array.from(new Set(matches.map((m) => parseInt(m[1], 10)))).sort((a, b) => a - b);
    return indices;
  }, [body]);

  // Insert sequential variable at current cursor or end
  const handleInsertVariable = () => {
    const nextIndex = detectedVariables.length > 0 ? Math.max(...detectedVariables) + 1 : 1;
    const token = `{{${nextIndex}}}`;
    setBody((prev) => (prev ? `${prev} ${token}` : token));
  };

  // Sanitize template name on input: lowercase, numbers, underscores only
  const handleTemplateNameChange = (val: string) => {
    const sanitized = val
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
    setTemplateName(sanitized);
    setFormError(null);
  };

  const handleAddButton = () => {
    const trimmedText = newButtonText.trim();
    if (!trimmedText) {
      showErrorToast("Please enter text for the button.");
      return;
    }
    if (trimmedText.length > 25) {
      showErrorToast("Button text cannot exceed 25 characters.");
      return;
    }
    if (buttons.length >= 3) {
      showErrorToast("You can add a maximum of 3 buttons in total.");
      return;
    }

    if (buttonType === "URL") {
      const urlCount = buttons.filter((b) => b.type === "URL").length;
      if (urlCount >= 2) {
        showErrorToast("Meta allows a maximum of 2 URL buttons per template.");
        return;
      }
      const trimmedUrl = newButtonUrl.trim();
      if (!trimmedUrl) {
        showErrorToast("Please enter the website URL (e.g., https://yourstudio.com).");
        return;
      }
      if (!/^https?:\/\/.+/i.test(trimmedUrl)) {
        showErrorToast("Please enter a valid URL starting with https:// or http://");
        return;
      }
      setButtons([...buttons, { type: "URL", text: trimmedText, url: trimmedUrl }]);
      setNewButtonText("");
      setNewButtonUrl("");
    } else {
      setButtons([...buttons, { type: "QUICK_REPLY", text: trimmedText }]);
      setNewButtonText("");
    }
  };

  const handleRemoveButton = (idx: number) => {
    setButtons(buttons.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    setFormError(null);

    if (!templateName.trim()) {
      const msg = "Please provide a template name (e.g., launch_announcement).";
      setFormError(msg);
      showErrorToast(msg);
      return;
    }

    if (!body.trim()) {
      const msg = "Please write the message body for your template.";
      setFormError(msg);
      showErrorToast(msg);
      return;
    }

    if (body.length > 1024) {
      const msg = "Template body exceeds the 1024 character limit set by WhatsApp.";
      setFormError(msg);
      showErrorToast(msg);
      return;
    }

    // Ensure all detected variables have sample values
    const samplesArray: string[] = [];
    for (const vIndex of detectedVariables) {
      const val = sampleValues[vIndex]?.trim();
      if (!val) {
        const msg = `Please enter a sample value for variable {{${vIndex}}} for Meta's review.`;
        setFormError(msg);
        showErrorToast(msg);
        return;
      }
      samplesArray.push(val);
    }

    setIsSubmitting(true);
    try {
      const res = await createTemplate({
        template_name: templateName.trim(),
        category: "UTILITY",
        language: "en",
        message_purpose: messagePurpose.trim() || undefined,
        body: body.trim(),
        sample_values: samplesArray,
        footer: footer.trim() || undefined,
        buttons: buttons.length > 0 ? buttons : undefined,
      });

      if (res && !(res as any).error) {
        // Reset form
        setTemplateName("");
        setBody("");
        setSampleValues({});
        setFooter("");
        setButtons([]);
        setNewButtonText("");
        setNewButtonUrl("");
        setButtonType("QUICK_REPLY");
        setMessagePurpose("");
        setShowCreate(false);
      } else {
        const errMsg =
          (res as any)?.payload?.message ||
          (res as any)?.error?.message ||
          "Failed to create template.";
        setFormError(errMsg);
        showErrorToast(errMsg);
      }
    } catch (err: any) {
      const errMsg = err?.message || "Failed to create template.";
      setFormError(errMsg);
      showErrorToast(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalTemplate) return;
    await deleteTemplate(deleteModalTemplate.id);
    setDeleteModalTemplate(null);
  };

  // Stats calculation
  const stats = useMemo(() => {
    const approved = templates.filter((t) => (t.status || "").toLowerCase() === "approved").length;
    const pending = templates.filter((t) => {
      const s = (t.status || "").toLowerCase();
      return (
        s === "pending" ||
        s === "submitted" ||
        s === "pending_review" ||
        s.includes("pending") ||
        s.includes("review")
      );
    }).length;
    const rejected = templates.filter((t) => (t.status || "").toLowerCase() === "rejected").length;
    return { total: templates.length, approved, pending, rejected };
  }, [templates]);

  // Filtered templates list
  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      const st = (t.status || "").toLowerCase();
      const isApproved = st === "approved";
      const isPending =
        st === "pending" ||
        st === "submitted" ||
        st === "pending_review" ||
        st.includes("pending") ||
        st.includes("review");
      const isRejected = st === "rejected";

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "approved" && isApproved) ||
        (statusFilter === "pending" && isPending) ||
        (statusFilter === "rejected" && isRejected);

      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (t.template_name || t.name || "").toLowerCase().includes(q) ||
        (t.body_text || t.body || "").toLowerCase().includes(q) ||
        (t.message_purpose || "").toLowerCase().includes(q) ||
        (t.category || "").toLowerCase().includes(q);

      return matchesStatus && matchesSearch;
    });
  }, [templates, statusFilter, searchQuery]);

  // Live preview text generator with sample values or placeholder badges
  const renderPreviewContent = () => {
    if (!body) return <PlaceholderText>Type your template message above to see how it looks to your clients.</PlaceholderText>;

    let rendered = body;
    detectedVariables.forEach((idx) => {
      const val = sampleValues[idx];
      const replacement = val ? val : `[Sample {{${idx}}}]`;
      rendered = rendered.split(`{{${idx}}}`).join(replacement);
    });

    return rendered;
  };

  return (
    <Page>
      {/* Top Hero Banner */}
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackBtn to="/dashboard/whatsapp" aria-label="Back to WhatsApp CRM">
              <ArrowLeft size={18} />
            </BackBtn>
            <HeroText>
              <HeroBadge>
                <Sparkles size={12} />
                WhatsApp Business Templates
              </HeroBadge>
              <PageTitle className="linear-text">Message Templates</PageTitle>
              <PageSubtitle>
                Create, customize, and sync Meta-approved message templates for your studio campaigns and automated client updates.
              </PageSubtitle>
            </HeroText>
          </HeroLeft>

          <HeroActions>
            <RefreshIconButton
              type="button"
              onClick={() => refresh(statusFilter !== "all" ? statusFilter : undefined)}
              title="Refresh templates"
            >
              <RefreshCw size={16} />
            </RefreshIconButton>
            <CreateBtn type="button" onClick={() => setShowCreate((v) => !v)}>
              {showCreate ? <X size={16} /> : <Plus size={16} />}
              {showCreate ? "Close Creator" : "New Template"}
            </CreateBtn>
          </HeroActions>
        </HeroContent>
      </Hero>

      {/* Stats Summary Strip */}
      <StatsStrip>
        <StatChip>
          <StatIcon $tone="#c94daa">
            <FileText size={18} />
          </StatIcon>
          <div>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Total Templates</StatLabel>
          </div>
        </StatChip>
        <StatChip>
          <StatIcon $tone="#16a34a">
            <CheckCircle2 size={18} />
          </StatIcon>
          <div>
            <StatValue>{stats.approved}</StatValue>
            <StatLabel>Approved & Ready</StatLabel>
          </div>
        </StatChip>
        <StatChip>
          <StatIcon $tone="#d97706">
            <Clock size={18} />
          </StatIcon>
          <div>
            <StatValue>{stats.pending}</StatValue>
            <StatLabel>In Meta Review</StatLabel>
          </div>
        </StatChip>
        <StatChip>
          <StatIcon $tone="#dc2626">
            <XCircle size={18} />
          </StatIcon>
          <div>
            <StatValue>{stats.rejected}</StatValue>
            <StatLabel>Needs Revision</StatLabel>
          </div>
        </StatChip>
      </StatsStrip>

      {/* Backend / Global Error Banner */}
      {isError && error?.templates && <ErrorBanner>{error.templates}</ErrorBanner>}

      {/* Template Creator Panel */}
      {showCreate && (
        <CreatePanel>
          <PanelHeader>
            <PanelTitleRow>
              <Plus size={18} color="#c94daa" />
              <div>
                <PanelTitle>Create New WhatsApp Template</PanelTitle>
                <PanelSubtitle>
                  Define your template text and sample values. Meta reviews submissions to ensure quality and compliance.
                </PanelSubtitle>
              </div>
            </PanelTitleRow>
            <CloseCreatorBtn type="button" onClick={() => setShowCreate(false)}>
              <X size={18} />
            </CloseCreatorBtn>
          </PanelHeader>

          {formError && (
            <FormAlertBox>
              <AlertCircle size={16} />
              <span>{formError}</span>
            </FormAlertBox>
          )}

          <CreateBodyGrid>
            {/* Form Fields Column */}
            <FormColumn>
              {/* Template Name */}
              <Field>
                <LabelRow>
                  <Label>Template Name / Identifier *</Label>
                  <FieldHint>lowercase, numbers & _ only</FieldHint>
                </LabelRow>
                <Input
                  placeholder="e.g. diwali_photo_offer_2026"
                  value={templateName}
                  onChange={(e) => handleTemplateNameChange(e.target.value)}
                />
                <HelperText>
                  This identifier is registered with Meta. Spaces are automatically replaced with underscores.
                </HelperText>
              </Field>

              {/* Internal Purpose / Notes */}
              <Field>
                <Label>Internal Purpose / Notes (Optional)</Label>
                <Input
                  placeholder="e.g. Broadcast for wedding photography packages"
                  value={messagePurpose}
                  onChange={(e) => setMessagePurpose(e.target.value)}
                />
              </Field>

              {/* Message Body */}
              <Field>
                <LabelRow>
                  <Label>Message Body *</Label>
                  <CharCounter $overLimit={body.length > 1024}>
                    {body.length} / 1024 characters
                  </CharCounter>
                </LabelRow>

                <BodyToolbar>
                  <InsertVarBtn type="button" onClick={handleInsertVariable}>
                    <Plus size={13} />
                    Insert Next Variable ({`{{${detectedVariables.length + 1}}}`})
                  </InsertVarBtn>
                  <ToolbarInfo>
                    Variables will be replaced dynamically when launching a campaign.
                  </ToolbarInfo>
                </BodyToolbar>

                <TextArea
                  placeholder="Hi {{1}}, thank you for choosing our studio! Your photos for {{2}} are ready. Tap below to view your gallery."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={5}
                />
              </Field>

              {/* Dynamic Sample Values (Required by Meta for approval) */}
              {detectedVariables.length > 0 && (
                <SampleValuesSection>
                  <SectionTitleBar>
                    <InfoIconWrap>
                      <HelpCircle size={15} />
                    </InfoIconWrap>
                    <div>
                      <SectionTitle>Sample Values for Meta Approval</SectionTitle>
                      <SectionSubtitle>
                        Meta requires realistic examples of each variable to review your template.
                      </SectionSubtitle>
                    </div>
                  </SectionTitleBar>

                  <SampleInputsGrid>
                    {detectedVariables.map((vIdx) => (
                      <SampleField key={vIdx}>
                        <SampleLabel>
                          Sample for <strong>{`{{${vIdx}}}`}</strong>
                        </SampleLabel>
                        <Input
                          placeholder={`e.g. ${
                            vIdx === 1 ? "Rahul Sharma" : vIdx === 2 ? "Wedding Album" : "25% Off"
                          }`}
                          value={sampleValues[vIdx] || ""}
                          onChange={(e) =>
                            setSampleValues({
                              ...sampleValues,
                              [vIdx]: e.target.value,
                            })
                          }
                        />
                      </SampleField>
                    ))}
                  </SampleInputsGrid>
                </SampleValuesSection>
              )}

              {/* Footer Text (Optional) */}
              <Field>
                <LabelRow>
                  <Label>Footer Text (Optional)</Label>
                  <CharCounter $overLimit={footer.length > 60}>{footer.length} / 60</CharCounter>
                </LabelRow>
                <Input
                  placeholder="e.g. Reply STOP to opt out"
                  maxLength={60}
                  value={footer}
                  onChange={(e) => setFooter(e.target.value)}
                />
              </Field>

              {/* Buttons (Optional) */}
              <Field>
                <LabelRow>
                  <Label>Buttons (Optional, max 3)</Label>
                  <FieldHint>{buttons.length} / 3 buttons</FieldHint>
                </LabelRow>

                {buttons.length < 3 && (
                  <>
                    <ButtonTypeToggle>
                      <ButtonTypeOption
                        type="button"
                        $active={buttonType === "QUICK_REPLY"}
                        onClick={() => setButtonType("QUICK_REPLY")}
                      >
                        <MessageSquare size={13} />
                        Quick Reply
                      </ButtonTypeOption>
                      <ButtonTypeOption
                        type="button"
                        $active={buttonType === "URL"}
                        onClick={() => setButtonType("URL")}
                      >
                        <Globe size={13} />
                        Visit Website (URL)
                      </ButtonTypeOption>
                    </ButtonTypeToggle>

                    {buttonType === "QUICK_REPLY" ? (
                      <ButtonAddRow>
                        <Input
                          placeholder="e.g. Contact Now or Know More"
                          value={newButtonText}
                          maxLength={25}
                          onChange={(e) => setNewButtonText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddButton();
                            }
                          }}
                        />
                        <AddButtonBtn type="button" onClick={handleAddButton} disabled={!newButtonText.trim()}>
                          <Plus size={15} />
                          Add Button
                        </AddButtonBtn>
                      </ButtonAddRow>
                    ) : (
                      <ButtonInputsGrid>
                        <Input
                          placeholder="Button text (e.g. Visit Website)"
                          value={newButtonText}
                          maxLength={25}
                          onChange={(e) => setNewButtonText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddButton();
                            }
                          }}
                        />
                        <Input
                          placeholder="Website URL (e.g. https://yourstudio.com)"
                          value={newButtonUrl}
                          onChange={(e) => setNewButtonUrl(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddButton();
                            }
                          }}
                        />
                        <AddButtonBtn
                          type="button"
                          onClick={handleAddButton}
                          disabled={!newButtonText.trim() || !newButtonUrl.trim()}
                        >
                          <Plus size={15} />
                          Add Button
                        </AddButtonBtn>
                      </ButtonInputsGrid>
                    )}

                    <ButtonHelpBox>
                      {buttonType === "QUICK_REPLY" ? (
                        <span>
                          <strong>Quick Reply:</strong> When clicked by the client, WhatsApp sends this text as an inbound reply to your studio, opening the 24-hour chat window.
                        </span>
                      ) : (
                        <span>
                          <strong>Visit Website:</strong> When clicked by the client, WhatsApp opens this website destination in the client&apos;s web browser.
                        </span>
                      )}
                    </ButtonHelpBox>
                  </>
                )}

                {buttons.length > 0 && (
                  <ButtonChipsList>
                    {buttons.map((btn, idx) => (
                      <ButtonChip key={idx} title={btn.type === "URL" ? btn.url : undefined}>
                        {btn.type === "URL" ? (
                          <Globe size={13} color="#2563eb" />
                        ) : (
                          <MessageSquare size={13} color="#00a884" />
                        )}
                        <span>{btn.text}</span>
                        {btn.type === "URL" && btn.url && (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#6b7280",
                              maxWidth: "180px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            ({btn.url})
                          </span>
                        )}
                        <RemoveChipBtn type="button" onClick={() => handleRemoveButton(idx)}>
                          <X size={13} />
                        </RemoveChipBtn>
                      </ButtonChip>
                    ))}
                  </ButtonChipsList>
                )}
              </Field>

              {/* Form Submission Actions */}
              <FormActions>
                <SecondaryBtn type="button" onClick={() => setShowCreate(false)}>
                  Cancel
                </SecondaryBtn>
                <PrimaryBtn
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !templateName.trim() || !body.trim()}
                >
                  {isSubmitting ? (
                    <>
                      <SpinningIcon>
                        <RefreshCw size={16} />
                      </SpinningIcon>
                      Submitting to Meta...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Submit for Meta Approval
                    </>
                  )}
                </PrimaryBtn>
              </FormActions>
            </FormColumn>

            {/* Live WhatsApp Mockup Preview Column */}
            <PreviewColumn>
              <PreviewContainer>
                <PhoneHeader>
                  <PhoneAvatar>KM</PhoneAvatar>
                  <div>
                    <PhoneTitle>Studio WhatsApp</PhoneTitle>
                    <PhoneStatus>Business Account</PhoneStatus>
                  </div>
                </PhoneHeader>

                <PhoneChatArea>
                  <ChatBubble>
                    <ChatBody>{renderPreviewContent()}</ChatBody>

                    {footer && <ChatFooter>{footer}</ChatFooter>}

                    <ChatMeta>
                      <span>12:00 PM</span>
                      <DoubleCheckMark>✓✓</DoubleCheckMark>
                    </ChatMeta>
                  </ChatBubble>

                  {buttons.length > 0 && (
                    <PreviewButtonsList>
                      {buttons.map((btn, i) => (
                        <PreviewButtonPill key={i}>
                          {btn.type === "URL" ? (
                            <ExternalLink size={12} />
                          ) : (
                            <MessageSquare size={12} />
                          )}
                          <span>{btn.text}</span>
                        </PreviewButtonPill>
                      ))}
                    </PreviewButtonsList>
                  )}
                </PhoneChatArea>

                <PreviewFooterNote>
                  <Eye size={13} />
                  Live client view preview
                </PreviewFooterNote>
              </PreviewContainer>
            </PreviewColumn>
          </CreateBodyGrid>
        </CreatePanel>
      )}

      {/* Templates List & Actions Panel */}
      <ListPanel>
        <PanelHeader>
          <PanelTitleRow>
            <FileText size={18} color="#c94daa" />
            <div>
              <PanelTitle>All Templates</PanelTitle>
              <PanelSubtitle>
                {filteredTemplates.length} of {templates.length} template
                {templates.length !== 1 ? "s" : ""}
              </PanelSubtitle>
            </div>
          </PanelTitleRow>

          <SearchFilterRow>
            <SearchBox>
              <Search size={14} color="#9ca3af" />
              <SearchInput
                placeholder="Search templates by name or text..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <ClearSearchBtn onClick={() => setSearchQuery("")}>
                  <X size={12} />
                </ClearSearchBtn>
              )}
            </SearchBox>

            <FilterTabs>
              {STATUS_TABS.map((tab) => (
                <FilterTabBtn
                  key={tab.value}
                  type="button"
                  $active={statusFilter === tab.value}
                  onClick={() => setStatusFilter(tab.value)}
                >
                  {tab.label}
                </FilterTabBtn>
              ))}
            </FilterTabs>
          </SearchFilterRow>
        </PanelHeader>

        <ListBody>
          {loading ? (
            <SkeletonListRows count={4} />
          ) : filteredTemplates.length === 0 ? (
            <EmptyWrap>
              <EmptyState
                icon={<FileText size={48} />}
                title={searchQuery ? "No matching templates" : "No templates found"}
                description={
                  searchQuery
                    ? "Try adjusting your search terms or status filter."
                    : "Create a template and submit it for Meta WhatsApp approval. Once approved, you can launch broadcast campaigns to your customers."
                }
              />
            </EmptyWrap>
          ) : (
            <TemplateListGrid>
              {filteredTemplates.map((t) => {
                const isApproved = (t.status || "").toLowerCase() === "approved";
                const isPending =
                  (t.status || "").toLowerCase() === "pending" ||
                  (t.status || "").toLowerCase() === "submitted" ||
                  (t.status || "").toLowerCase() === "pending_review" ||
                  (t.status || "").toLowerCase().includes("pending") ||
                  (t.status || "").toLowerCase().includes("review");
                const isRejected = (t.status || "").toLowerCase() === "rejected";
                const isSyncing = syncingTemplateId === t.id;
                const isDeleting = deletingTemplateId === t.id;

                const templateBody = t.body_text || t.body || "";
                const templateFooter = t.footer_text || t.footer || "";

                return (
                  <TemplateCard key={t.id} $isApproved={isApproved}>
                    <TemplateTop>
                      <TemplateHeaderLeft>
                        <TemplateDisplayName>
                          {t.template_name || t.name}
                        </TemplateDisplayName>
                        <PillsRow>
                          <CategoryBadge $category={t.category || "MARKETING"}>
                            {t.category || "MARKETING"}
                          </CategoryBadge>
                          {t.language && <LanguageBadge>{t.language.toUpperCase()}</LanguageBadge>}
                        </PillsRow>
                      </TemplateHeaderLeft>

                      <StatusBadge $status={t.status || "pending"}>
                        {isApproved && <CheckCircle2 size={13} />}
                        {isPending && <Clock size={13} />}
                        {isRejected && <XCircle size={13} />}
                        {isApproved ? "Approved" : isPending ? "In Review" : isRejected ? "Rejected" : t.status}
                      </StatusBadge>
                    </TemplateTop>

                    {t.message_purpose && (
                      <TemplatePurposeBox>
                        <FileText size={12} color="#c94daa" style={{ flexShrink: 0, marginTop: 1 }} />
                        <TemplatePurposeText>
                          <strong>Note:</strong> {t.message_purpose}
                        </TemplatePurposeText>
                      </TemplatePurposeBox>
                    )}

                    <TemplateBodyBox>{templateBody}</TemplateBodyBox>

                    {templateFooter && <TemplateFooterBox>{templateFooter}</TemplateFooterBox>}

                    {t.buttons && t.buttons.length > 0 && (
                      <TemplateButtonsRow>
                        {t.buttons.map((btn, bIdx) => (
                          <MiniButtonChip key={bIdx} title={btn.type === "URL" && btn.url ? `URL: ${btn.url}` : undefined}>
                            {btn.type === "URL" ? (
                              <ExternalLink size={11} />
                            ) : (
                              <MessageSquare size={11} />
                            )}
                            <span>{btn.text}</span>
                          </MiniButtonChip>
                        ))}
                      </TemplateButtonsRow>
                    )}

                    <TemplateActionsBar>
                      {/* Meta Sync Action */}
                      <ActionBtn
                        type="button"
                        onClick={() => syncTemplate(t.id)}
                        disabled={isSyncing}
                        title="Sync latest status and category with Meta"
                      >
                        <RefreshCw size={13} className={isSyncing ? "spin-animation" : ""} />
                        {isSyncing ? "Syncing..." : "Sync"}
                      </ActionBtn>

                      {/* Delete Action */}
                      <DeleteActionBtn
                        type="button"
                        onClick={() => setDeleteModalTemplate(t)}
                        disabled={isDeleting}
                        title="Delete template"
                      >
                        <Trash2 size={13} />
                        Delete
                      </DeleteActionBtn>

                      {/* Launch Campaign shortcut for approved templates */}
                      {isApproved && (
                        <LaunchCampaignShortcutBtn
                          type="button"
                          onClick={() => navigate(`/dashboard/whatsapp/campaigns?template_id=${t.id}`)}
                          title="Use in Campaign broadcast"
                        >
                          <Send size={12} />
                          Use in Campaign
                        </LaunchCampaignShortcutBtn>
                      )}
                    </TemplateActionsBar>
                  </TemplateCard>
                );
              })}
            </TemplateListGrid>
          )}
        </ListBody>
      </ListPanel>

      {/* Delete Confirmation Modal */}
      {deleteModalTemplate && (
        <ModalBackdrop onClick={() => setDeleteModalTemplate(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeaderRow>
              <AlertCircle size={22} color="#dc2626" />
              <ModalHeading>Delete Template?</ModalHeading>
            </ModalHeaderRow>
            <ModalText>
              Are you sure you want to delete the template{" "}
              <strong>"{deleteModalTemplate.template_name || deleteModalTemplate.name}"</strong>?
              This action cannot be undone.
            </ModalText>

            <ModalBtnRow>
              <SecondaryBtn type="button" onClick={() => setDeleteModalTemplate(null)}>
                Cancel
              </SecondaryBtn>
              <DeleteConfirmBtn type="button" onClick={handleConfirmDelete}>
                Delete Template
              </DeleteConfirmBtn>
            </ModalBtnRow>
          </ModalBox>
        </ModalBackdrop>
      )}
    </Page>
  );
};

export default TemplateManagerPage;

